import { ipfsParseMetadataOrUri, ipfsUriToMetadata, normalizeCharacterMetadata } from './chunk-XMIRV277.mjs';
import { addressToAccount, createPublicClientFromChain, createWalletClientFromPrivateKeyAccount, getProviderAccount, createWalletClientFromProvider, entry, linklist, newbieVilla, periphery, cbt, mira, tips, tipsWithConfig, tipsWithFee, validateAddress, waitForTransactionReceiptWithRetry, NIL_ADDRESS, parseLog, validateHandleFormat, getModuleConfig, warn } from './chunk-N4AJ44EV.mjs';
import { crossbell, crossbellTestnet, CONTRACT_ADDRESS } from './chunk-Z5ML27IN.mjs';
import { __export, __decorateClass } from './chunk-FHBV65QG.mjs';
import { getContract, erc20Abi, stringToHex, encodeFunctionData, hexToString, keccak256, encodePacked, encodeAbiParameters, isAddressEqual } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import pLimit from 'p-limit';

// src/contract/subcontracts/index.ts
var subcontracts_exports = {};
__export(subcontracts_exports, {
  BaseContract: () => BaseContract,
  CbtContract: () => CbtContract,
  CharacterContract: () => CharacterContract,
  CsbContract: () => CsbContract,
  LinkContract: () => LinkContract,
  LinkModuleContract: () => LinkModuleContract,
  MintModuleContract: () => MintModuleContract,
  NoteContract: () => NoteContract,
  OperatorContract: () => OperatorContract,
  RevisionContract: () => RevisionContract,
  TipsContract: () => TipsContract
});
var BaseContract = class {
  publicClient;
  walletClient;
  #account;
  get account() {
    return this.#account;
  }
  set account(value) {
    this.#account = typeof value === "string" ? addressToAccount(value) : value;
    if (this.walletClient)
      this.walletClient.account = this.#account;
  }
  options;
  contract;
  linklistContract;
  newbieVillaContract;
  peripheryContract;
  cbtContract;
  miraContract;
  tipsContract;
  tipsWithConfigContract;
  tipsWithFeeContract;
  /**
   * This creates a new Contract instance to interact with.
   * @param providerOrPrivateKey - The provider or private key to connect to the contract.
   * @returns The Contract instance.
   *
   * @example Connect with Metamask
   * ```js
   * import { Contract } from 'crossbell'
   * const provider = window.ethereum // the metamask provider
   * const contract = new Contract(provider)
   * ```
   *
   * @example Connect with Private Key
   * ```js
   * import { Contract } from 'crossbell'
   * const privateKey = '0xabcdef0123456789012345678901234567890123456789012345678901234'
   * const contract = new Contract(privateKey)
   * ```
   *
   * @example Connect with a Readonly Contract
   * ```js
   * import { Contract } from 'crossbell'
   * const contract = new Contract() // readonly contract
   * ```
   */
  constructor(providerOrPrivateKey, options) {
    this.options = this.#resolveOptions(options);
    this.#initPublicClient();
    this.#initWalletClient(providerOrPrivateKey);
    this.#initContract();
  }
  #resolveOptions(options) {
    const chain = options?.chain === "mainnet" ? crossbell : options?.chain === "testnet" ? crossbellTestnet : options?.chain ?? crossbell;
    return {
      account: this.account,
      gasPrice: options?.gasPrice ?? 10n ** 9n,
      gCsbAsGas: options?.gCsbAsGas ?? "asDefault",
      contractAddresses: {
        entry: CONTRACT_ADDRESS.ENTRY,
        periphery: CONTRACT_ADDRESS.PERIPHERY,
        cbt: CONTRACT_ADDRESS.CBT,
        tips: CONTRACT_ADDRESS.TIPS,
        tipsWithConfig: CONTRACT_ADDRESS.TIPS_WITH_CONFIG,
        tipsWithFee: CONTRACT_ADDRESS.TIPS_WITH_FEE,
        mira: CONTRACT_ADDRESS.MIRA,
        newbieVilla: CONTRACT_ADDRESS.NEWBIE_VILLA,
        linklist: CONTRACT_ADDRESS.LINKLIST,
        gcsb: CONTRACT_ADDRESS.GCSB,
        ...options?.contractAddresses
      },
      chain,
      rpcUrl: options?.rpcUrl ?? chain.rpcUrls.default.http[0]
    };
  }
  #initPublicClient() {
    this.publicClient = createPublicClientFromChain(
      this.options.chain,
      this.options.rpcUrl
    );
  }
  #initWalletClient(providerOrPrivateKey) {
    if (typeof providerOrPrivateKey === "string") {
      const privateKeyAccount = privateKeyToAccount(providerOrPrivateKey);
      this.#account = privateKeyAccount;
      this.walletClient = createWalletClientFromPrivateKeyAccount(
        privateKeyAccount,
        this.options.chain,
        this.options.rpcUrl
      );
    } else if (providerOrPrivateKey && typeof providerOrPrivateKey === "object") {
      const provider = providerOrPrivateKey;
      this.#account = this.options.account ? addressToAccount(this.options.account) : getProviderAccount(provider);
      if (!this.options?.account) {
        provider.on("accountsChanged", (accounts) => {
          this.account = accounts[0];
        });
      }
      this.walletClient = createWalletClientFromProvider(
        provider,
        this.account,
        this.options.chain
      );
    }
  }
  #initContract() {
    this.contract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.entry,
        abi: entry,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.linklistContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.linklist,
        abi: linklist,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.newbieVillaContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.newbieVilla,
        abi: newbieVilla,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.peripheryContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.periphery,
        abi: periphery,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.cbtContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.cbt,
        abi: cbt,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.miraContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.mira,
        abi: mira,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.tipsContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.tips,
        abi: tips,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.tipsWithConfigContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.tipsWithConfig,
        abi: tipsWithConfig,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
    this.tipsWithFeeContract = this.#proxyContract(
      getContract({
        address: this.options.contractAddresses.tipsWithFee,
        abi: tipsWithFee,
        client: {
          public: this.publicClient,
          wallet: this.walletClient
        }
      })
    );
  }
  async #hasEnoughGCsb() {
    const balance = await this.publicClient.readContract({
      abi: erc20Abi,
      functionName: "balanceOf",
      args: [this.account.address],
      address: this.options.contractAddresses.gcsb
    });
    return balance >= 10n ** 18n;
  }
  #proxyContract(contract) {
    if (contract.write)
      contract.write = new Proxy(contract.write, {
        get: (...args) => {
          return new Proxy(Reflect.get(...args), {
            apply: async (target, thisArg, argArray) => {
              const hasArgs = argArray.length > 0 && Array.isArray(argArray[0]);
              const options = {
                ...(hasArgs ? argArray[1] : argArray[0]) ?? {}
              };
              if (this.options.chain.id === crossbellTestnet.id) {
                if (!options.gasPrice) {
                  if (typeof this.options.gasPrice === "bigint") {
                    options.gasPrice = this.options.gasPrice;
                  }
                  if (this.options.gCsbAsGas === true) {
                    options.gasPrice = 0n;
                  } else if (this.options.gCsbAsGas === false) ; else if (this.options.gCsbAsGas === "asDefault") {
                    if (await this.#hasEnoughGCsb()) {
                      options.gasPrice = 0n;
                    }
                  }
                }
              } else {
                if (typeof this.options.gasPrice === "bigint") {
                  options.gasPrice = this.options.gasPrice;
                }
              }
              argArray[hasArgs ? 1 : 0] = options;
              return Reflect.apply(target, thisArg, argArray);
            }
          });
        }
      });
    return contract;
  }
};

// src/contract/decorators/index.ts
function autoSwitchMainnet() {
  return (target, propertyKey, descriptor) => {
    const originalMethod = descriptor.value;
    descriptor.value = async function(...args) {
      const checkAndSwitch = async () => {
        const walletClient = this.base.walletClient;
        if (!walletClient)
          return;
        const currentChainId = await walletClient.getChainId();
        const targetChainId = this.base.walletClient.chain.id;
        const isCorrectChain = currentChainId === targetChainId;
        if (!isCorrectChain) {
          warn(
            "You're not on the crossbell chain. Switching to crossbell chain."
          );
          await walletClient.switchChain({ id: targetChainId });
        }
      };
      try {
        await checkAndSwitch();
      } catch {
        await checkAndSwitch();
      }
      return originalMethod?.apply(this, args);
    };
    return descriptor;
  };
}

// src/contract/subcontracts/csb.ts
var CsbContract = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * It returns the $CSB balance of the owner.
   * @category CSB
   * @returns The $CSB balance of the owner.
   */
  async getBalance({
    owner
  }) {
    validateAddress(owner);
    const balance = await this.base.publicClient.getBalance({
      address: owner
    });
    return {
      data: balance
    };
  }
  async transfer({
    toAddress,
    amount
  }) {
    validateAddress(toAddress);
    if (!this.base.walletClient) {
      throw new Error(
        "No walletClient found. You must be connected to a wallet to transfer $CSB. Currently You might be using a read-only provider."
      );
    }
    const hash = await this.base.walletClient.sendTransaction({
      account: this.base.account,
      to: toAddress,
      value: BigInt(amount)
    });
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: {},
      transactionHash: receipt.transactionHash
    };
  }
};
__decorateClass([
  autoSwitchMainnet()
], CsbContract.prototype, "transfer", 1);
var LinkContract = class {
  constructor(base) {
    this.base = base;
  }
  async linkCharacter({
    fromCharacterId,
    toCharacterId,
    linkType,
    data
  }, overrides = {}) {
    const hash = await this.base.contract.write.linkCharacter(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toCharacterId: BigInt(toCharacterId),
          linkType: stringToHex(linkType, { size: 32 }),
          data: data ?? NIL_ADDRESS
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const parser = parseLog(receipt.logs, "LinkCharacter");
    return {
      data: parser.args.linklistId,
      transactionHash: receipt.transactionHash
    };
  }
  async linkCharactersInBatch({
    fromCharacterId,
    toCharacterIds,
    toAddresses,
    linkType,
    data
  }, overrides = {}) {
    validateAddress(toAddresses);
    const hash = await this.base.peripheryContract.write.linkCharactersInBatch(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toCharacterIds: toCharacterIds.map((id) => BigInt(id)),
          toAddresses,
          linkType: stringToHex(linkType, { size: 32 }),
          data: data ?? toCharacterIds.map(() => NIL_ADDRESS)
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "LinkCharacter", {
      throwOnMultipleLogsFound: false
    });
    return {
      data: log.args.linklistId,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This gets the linklist id of a {@link linkCharacter} transaction.
   * @category Link
   * @returns The linklist id of the transaction.
   */
  async getLinklistIdByTransaction({
    hash
  }) {
    const receipt = await this.base.publicClient.getTransactionReceipt({ hash });
    const parser = parseLog(receipt.logs, "LinkCharacter");
    return {
      data: parser.args.linklistId
    };
  }
  async createThenLinkCharacter({
    fromCharacterId,
    toAddress,
    linkType
  }, overrides = {}) {
    validateAddress(toAddress);
    const hash = await this.base.contract.write.createThenLinkCharacter(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          to: toAddress,
          linkType: stringToHex(linkType, { size: 32 })
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const createCharacterParser = parseLog(receipt.logs, "CharacterCreated");
    const linkCharacterParser = parseLog(receipt.logs, "LinkCharacter");
    return {
      data: {
        toCharacterId: createCharacterParser.args.characterId,
        linklistId: linkCharacterParser.args.linklistId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async unlinkCharacter({
    fromCharacterId,
    toCharacterId,
    linkType
  }, overrides = {}) {
    const hash = await this.base.contract.write.unlinkCharacter(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toCharacterId: BigInt(toCharacterId),
          linkType: stringToHex(linkType, { size: 32 })
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async getLinkingCharacterIds({
    linklistId,
    fromCharacterId,
    linkType
  }, overrides = {}) {
    if (linklistId) {
      const linklist3 = await this.base.linklistContract.read.getLinkingCharacterIds(
        [BigInt(linklistId)],
        overrides
      );
      return {
        data: linklist3.map((link) => link)
      };
    }
    const linklist2 = await this.base.peripheryContract.read.getLinkingCharacterIds(
      [BigInt(fromCharacterId), stringToHex(linkType, { size: 32 })],
      overrides
    );
    return {
      data: linklist2.map((link) => link)
    };
  }
  /**
   * This returns the *attached* linked character of a character with a given link type.
   * @category Link
   * @returns An array of character that are linked to the character id passed in.
   */
  async getLinkingCharacters({
    fromCharacterId,
    linkType
  }, overrides = {}) {
    const ids = await this.base.peripheryContract.read.getLinkingCharacterIds(
      [BigInt(fromCharacterId), stringToHex(linkType, { size: 32 })],
      overrides
    );
    const characters = await Promise.all(
      ids.map((ids2) => this.base.character.get({ characterId: ids2 }))
    );
    return {
      data: characters.map((character) => character.data)
    };
  }
  async linkAddress({
    fromCharacterId,
    toAddress,
    linkType,
    data = NIL_ADDRESS
  }, overrides = {}) {
    const hash = await this.base.contract.write.linkAddress(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          ethAddress: toAddress,
          linkType: stringToHex(linkType, { size: 32 }),
          data
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const parser = parseLog(receipt.logs, "LinkAddress");
    return {
      data: parser.args.linklistId,
      transactionHash: receipt.transactionHash
    };
  }
  async unlinkAddress({
    fromCharacterId,
    toAddress,
    linkType
  }, overrides = {}) {
    const hash = await this.base.contract.write.unlinkAddress(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          ethAddress: toAddress,
          linkType: stringToHex(linkType, { size: 32 })
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async linkAnyUri({
    fromCharacterId,
    toUri,
    linkType,
    data = NIL_ADDRESS
  }, overrides = {}) {
    const hash = await this.base.contract.write.linkAnyUri(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toUri,
          linkType: stringToHex(linkType, { size: 32 }),
          data
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const parser = parseLog(receipt.logs, "LinkAnyUri");
    return {
      data: parser.args.linklistId,
      transactionHash: receipt.transactionHash
    };
  }
  async unlinkAnyUri({
    fromCharacterId,
    toUri,
    linkType
  }, overrides = {}) {
    const hash = await this.base.contract.write.unlinkAnyUri(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toUri,
          linkType: stringToHex(linkType, { size: 32 })
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async linkErc721({
    fromCharacterId,
    toContractAddress,
    toTokenId,
    linkType,
    data = NIL_ADDRESS
  }, overrides = {}) {
    const hash = await this.base.contract.write.linkERC721(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          tokenAddress: toContractAddress,
          tokenId: BigInt(toTokenId),
          linkType: stringToHex(linkType, { size: 32 }),
          data
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const parser = parseLog(receipt.logs, "LinkAnyUri");
    return {
      data: parser.args.linklistId,
      transactionHash: receipt.transactionHash
    };
  }
  async unlinkErc721({
    fromCharacterId,
    toContractAddress,
    toTokenId,
    linkType
  }, overrides = {}) {
    const hash = await this.base.contract.write.unlinkERC721(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          tokenAddress: toContractAddress,
          tokenId: BigInt(toTokenId),
          linkType: stringToHex(linkType, { size: 32 })
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async linkNote({
    fromCharacterId,
    toCharacterId,
    toNoteId,
    linkType,
    data
  }, overrides = {}) {
    const hash = await this.base.contract.write.linkNote(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toCharacterId: BigInt(toCharacterId),
          toNoteId: BigInt(toNoteId),
          linkType: stringToHex(linkType, { size: 32 }),
          data: data ?? NIL_ADDRESS
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const parser = parseLog(receipt.logs, "LinkNote");
    return {
      data: parser.args.linklistId,
      transactionHash: receipt.transactionHash
    };
  }
  async unlinkNote({
    fromCharacterId,
    toCharacterId,
    toNoteId,
    linkType
  }, overrides = {}) {
    const hash = await this.base.contract.write.unlinkNote(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toCharacterId: BigInt(toCharacterId),
          toNoteId: BigInt(toNoteId),
          linkType: stringToHex(linkType, { size: 32 })
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async linkLinklist({
    fromCharacterId,
    toLinkListId,
    linkType,
    data = NIL_ADDRESS
  }, overrides = {}) {
    const hash = await this.base.contract.write.linkLinklist(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toLinkListId: BigInt(toLinkListId),
          linkType: stringToHex(linkType, { size: 32 }),
          data
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const parser = parseLog(receipt.logs, "LinkNote");
    return {
      data: parser.args.linklistId,
      transactionHash: receipt.transactionHash
    };
  }
  async unlinkLinklist({
    fromCharacterId,
    toLinklistId,
    linkType
  }, overrides = {}) {
    const hash = await this.base.contract.write.unlinkLinklist(
      [
        {
          fromCharacterId: BigInt(fromCharacterId),
          toLinkListId: BigInt(toLinklistId),
          linkType: stringToHex(linkType, { size: 32 })
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This sets the metadata for a linklist.
   * @category Link
   */
  async setLinklistUri({ linklistId, uri }, overrides = {}) {
    const hash = await this.base.contract.write.setLinklistUri(
      [BigInt(linklistId), uri],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This gets the metadata for a linklist.
   * @category Link
   */
  async getLinklistUri({ linklistId }, overrides = {}) {
    const uri = await this.base.contract.read.getLinklistUri(
      [BigInt(linklistId)],
      overrides
    );
    return {
      data: uri
    };
  }
  /**
   * This sets the type for a linklist.
   * @category Link
   */
  async setLinklistType({ linklistId, linkType }, overrides = {}) {
    const hash = await this.base.contract.write.setLinklistType(
      [BigInt(linklistId), stringToHex(linkType, { size: 32 })],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This burns a linklist.
   * @category Link
   * @returns The transaction hash of the transaction that was sent to the blockchain.
   */
  async burnLinklist({
    linklistId
  }, overrides = {}) {
    const hash = await this.base.contract.write.burnLinklist(
      [BigInt(linklistId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
};
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "linkCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "linkCharactersInBatch", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "createThenLinkCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "unlinkCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "linkAddress", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "unlinkAddress", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "linkAnyUri", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "unlinkAnyUri", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "linkErc721", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "unlinkErc721", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "linkNote", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "unlinkNote", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "linkLinklist", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkContract.prototype, "unlinkLinklist", 1);
async function buildPostNoteData({
  linkModule,
  mintModule,
  characterId,
  metadataOrUri,
  locked = false
}) {
  const { uri } = await ipfsParseMetadataOrUri("note", metadataOrUri);
  const linkModuleConfig = await getModuleConfig(linkModule);
  const mintModuleConfig = await getModuleConfig(mintModule);
  return {
    characterId: BigInt(characterId),
    contentUri: uri,
    linkModule: linkModuleConfig.address,
    linkModuleInitData: linkModuleConfig.initData,
    mintModule: mintModuleConfig.address,
    mintModuleInitData: mintModuleConfig.initData,
    locked
  };
}
var NoteContract = class {
  constructor(base) {
    this.base = base;
  }
  async post(options, overrides = {}) {
    const data = await buildPostNoteData(options);
    const hash = await this.base.contract.write.postNote([data], overrides);
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "PostNote");
    return {
      data: {
        noteId: log.args.noteId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async postMany({
    notes
  }, overrides = {}) {
    const limitedPromise = pLimit(10);
    const encodedDataArr = await Promise.all(
      notes.map(({ options, characterId, metadataOrUri }) => {
        return limitedPromise(async () => {
          const data = await buildPostNoteData({
            ...options,
            characterId,
            metadataOrUri
          });
          return encodeFunctionData({
            abi: entry,
            functionName: "postNote",
            args: [data]
          });
        });
      })
    );
    const hash = await this.base.contract.write.multicall(
      [encodedDataArr],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const logs = parseLog(receipt.logs, "PostNote", {
      throwOnMultipleLogsFound: false,
      returnMultipleLogs: true
    });
    const noteIds = logs.map((log) => log.args.noteId);
    return {
      data: {
        noteIds
      },
      transactionHash: receipt.transactionHash
    };
  }
  async postForAnyUri({
    targetUri,
    ...options
  }, overrides = {}) {
    const data = await buildPostNoteData(options);
    const hash = await this.base.contract.write.postNote4AnyUri(
      [data, targetUri],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "PostNote");
    return {
      data: {
        noteId: log.args.noteId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async postForNote({
    targetCharacterId,
    targetNoteId,
    ...options
  }, overrides = {}) {
    const data = await buildPostNoteData(options);
    const hash = await this.base.contract.write.postNote4Note(
      [
        data,
        {
          characterId: BigInt(targetCharacterId),
          noteId: BigInt(targetNoteId)
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "PostNote");
    return {
      data: {
        noteId: log.args.noteId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async postForCharacter({
    toCharacterId,
    ...options
  }, overrides = {}) {
    const data = await buildPostNoteData(options);
    const hash = await this.base.contract.write.postNote4Character(
      [data, BigInt(toCharacterId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "PostNote");
    return {
      data: {
        noteId: log.args.noteId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async postForLinklist({
    toLinklistId,
    ...options
  }, overrides = {}) {
    const data = await buildPostNoteData(options);
    const hash = await this.base.contract.write.postNote4Linklist(
      [data, BigInt(toLinklistId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "PostNote");
    return {
      data: {
        noteId: log.args.noteId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async postForAddress({
    toAddress,
    ...options
  }, overrides = {}) {
    const data = await buildPostNoteData(options);
    const hash = await this.base.contract.write.postNote4Address(
      [data, toAddress],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "PostNote");
    return {
      data: {
        noteId: log.args.noteId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async postForERC721({
    tokenAddress,
    tokenId,
    ...options
  }, overrides = {}) {
    const data = await buildPostNoteData(options);
    const hash = await this.base.contract.write.postNote4ERC721(
      [data, { tokenAddress, erc721TokenId: BigInt(tokenId) }],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "PostNote");
    return {
      data: {
        noteId: log.args.noteId
      },
      transactionHash: receipt.transactionHash
    };
  }
  async setUri({
    characterId,
    noteId,
    metadataOrUri
  }, overrides = {}) {
    const { uri, metadata } = await ipfsParseMetadataOrUri(
      "note",
      metadataOrUri,
      true
    );
    const hash = await this.base.contract.write.setNoteUri(
      [BigInt(characterId), BigInt(noteId), uri],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: {
        uri,
        metadata
      },
      transactionHash: receipt.transactionHash
    };
  }
  async changeMetadata({
    characterId,
    noteId,
    modifier
  }, overrides = {}) {
    const note = await this.get({ characterId, noteId });
    const metadata = modifier(note.data.metadata);
    if (typeof metadata === "undefined") {
      throw new TypeError(
        "The modified metadata is undefined. Did you return it?"
      );
    }
    if (!metadata.type) {
      metadata.type = "note";
    }
    return this.setMetadata({ characterId, noteId, metadata }, overrides);
  }
  /**
   * This is the same as {@link setUri}
   * @category Note
   */
  setMetadata({
    characterId,
    noteId,
    metadata
  }, overrides = {}) {
    return this.setUri(
      { characterId, noteId, metadataOrUri: metadata },
      overrides
    );
  }
  /**
   * This returns the info of a note.
   * @category Note
   * @returns The info of the note.
   */
  async get({
    characterId,
    noteId,
    linkItemType
  }, overrides = {}) {
    const data = await this.base.contract.read.getNote(
      [BigInt(characterId), BigInt(noteId)],
      overrides
    );
    const linkItemTypeString = hexToString(data.linkItemType, { size: 32 }) || void 0;
    const metadata = data.contentUri ? await ipfsUriToMetadata(data.contentUri) : void 0;
    let linkItem;
    const pc = this.base.peripheryContract.read;
    if (linkItemType === "AnyUri") {
      const uri = await pc.getLinkingAnyUri([data.linkKey]);
      linkItem = { uri };
    } else if (linkItemType === "ERC721") {
      const erc721 = await pc.getLinkingERC721([data.linkKey]);
      linkItem = {
        contractAddress: erc721.tokenAddress,
        tokenId: erc721.erc721TokenId.toString()
      };
    } else if (linkItemType === "Address") {
      const address = await pc.getLinkingAddress([data.linkKey]);
      linkItem = {
        address
      };
    } else if (linkItemType === "Character") {
      const characterId2 = await pc.getLinkingCharacterId([data.linkKey]);
      linkItem = {
        characterId: characterId2
      };
    } else if (linkItemType === "Note") {
      const ret = await pc.getLinkingNote([data.linkKey]);
      linkItem = {
        characterId: ret.characterId,
        noteId: ret.noteId
      };
    } else if (linkItemType === "Linklist") {
      const linklistId = await pc.getLinkingLinklistId([data.linkKey]);
      linkItem = {
        linklistId
      };
    } else {
      linkItem = void 0;
    }
    return {
      data: {
        characterId: BigInt(characterId),
        noteId: BigInt(noteId),
        contentUri: data.contentUri,
        metadata,
        linkItemType: data.linkItemType,
        linkItemTypeString,
        linkItem,
        linkKey: data.linkKey,
        linkModule: data.linkModule,
        contractAddress: data.mintNFT,
        mintModule: data.mintModule,
        deleted: data.deleted,
        locked: data.locked
      }
    };
  }
  async delete({
    characterId,
    noteId
  }, overrides = {}) {
    const hash = await this.base.contract.write.deleteNote(
      [BigInt(characterId), BigInt(noteId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async lock({
    characterId,
    noteId
  }, overrides = {}) {
    const hash = await this.base.contract.write.lockNote(
      [BigInt(characterId), BigInt(noteId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async mint({
    characterId,
    noteId,
    toAddress
  }, overrides = {}) {
    validateAddress(toAddress);
    const hash = await this.base.contract.write.mintNote(
      [
        {
          characterId: BigInt(characterId),
          noteId: BigInt(noteId),
          to: toAddress,
          mintModuleData: NIL_ADDRESS
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "MintNote");
    return {
      data: {
        contractAddress: log.args.tokenAddress,
        tokenId: log.args.tokenId
      },
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This returns the linkKey of a note linked to a character.
   * @category Note
   * @returns The linkKey of the note.
   */
  getLinkKeyForCharacter({
    toCharacterId
  }) {
    return keccak256(
      encodePacked(["string", "uint"], ["Character", BigInt(toCharacterId)])
    );
  }
  /**
   * This returns the linkKey of a note linked to a note.
   * @category Note
   * @returns The linkKey of the note.
   */
  getLinkKeyForAddress({
    toAddress
  }) {
    validateAddress(toAddress);
    return keccak256(
      encodePacked(["string", "address"], ["Address", toAddress])
    );
  }
  /**
   * This returns the linkKey of a note linked to a note.
   * @category Note
   * @returns The linkKey of the note.
   */
  getLinkKeyForNote({
    toCharacterId,
    toNoteId
  }) {
    return keccak256(
      encodePacked(
        ["string", "uint", "uint"],
        ["Note", BigInt(toCharacterId), BigInt(toNoteId)]
      )
    );
  }
  /**
   * This returns the linkKey of a note linked to an ERC721 token.
   * @category Note
   * @returns The linkKey of the note.
   */
  getLinkKeyForERC721({
    toContractAddress,
    toTokenId
  }) {
    return keccak256(
      encodePacked(
        ["string", "address", "uint"],
        ["ERC721", toContractAddress, BigInt(toTokenId)]
      )
    );
  }
  /**
   * This returns the linkKey of a note linked to a linklist.
   * @category Note
   * @returns The linkKey of the note.
   */
  getLinkKeyForLinklist({
    toLinkListId
  }) {
    return keccak256(
      encodePacked(["string", "uint"], ["Linklist", BigInt(toLinkListId)])
    );
  }
  /**
   * This returns the linkKey of a note linked to an any uri.
   * @category Note
   * @returns The linkKey of the note.
   */
  getLinkKeyForAnyUri({
    toUri
  }) {
    return keccak256(encodePacked(["string", "string"], ["AnyUri", toUri]));
  }
};
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "post", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "postMany", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "postForAnyUri", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "postForNote", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "postForCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "postForLinklist", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "postForAddress", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "postForERC721", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "setUri", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "changeMetadata", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "delete", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "lock", 1);
__decorateClass([
  autoSwitchMainnet()
], NoteContract.prototype, "mint", 1);

// src/contract/subcontracts/character.ts
var CharacterContract = class {
  constructor(base) {
    this.base = base;
  }
  async create({
    owner,
    handle,
    metadataOrUri,
    linkModule
  }, overrides = {}) {
    validateAddress(owner);
    const { valid, message } = validateHandleFormat(handle, {
      disallowAddress: true
    });
    if (!valid) {
      throw new TypeError(message);
    }
    const { uri } = await ipfsParseMetadataOrUri("character", metadataOrUri);
    const moduleConfig = await getModuleConfig(linkModule);
    const hash = await this.base.contract.write.createCharacter(
      [
        {
          to: owner,
          handle,
          uri,
          linkModule: moduleConfig.address,
          linkModuleInitData: moduleConfig.initData
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const parser = parseLog(receipt.logs, "CharacterCreated");
    return {
      data: parser.args.characterId,
      transactionHash: receipt.transactionHash
    };
  }
  async setHandle({
    characterId,
    handle
  }, overrides = {}) {
    const { valid, message } = validateHandleFormat(handle, {
      disallowAddress: true
    });
    if (!valid) {
      throw new TypeError(message);
    }
    const hash = await this.base.contract.write.setHandle(
      [BigInt(characterId), handle],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async setUri({
    characterId,
    metadataOrUri
  }, overrides = {}) {
    const { uri, metadata } = await ipfsParseMetadataOrUri(
      "character",
      metadataOrUri,
      true
    );
    const hash = await this.base.contract.write.setCharacterUri(
      [BigInt(characterId), uri],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: {
        uri,
        metadata
      },
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This is the same as {@link setUri}
   * @category Character
   */
  setMetadata({
    characterId,
    metadata
  }, overrides = {}) {
    return this.setUri({ characterId, metadataOrUri: metadata }, overrides);
  }
  async changeMetadata({
    characterId,
    modifier
  }, overrides = {}) {
    const character = await this.get({ characterId });
    const metadata = modifier(character.data.metadata);
    if (typeof metadata === "undefined") {
      throw new TypeError(
        "The modified metadata is undefined. Did you return it?"
      );
    }
    if (!metadata.type) {
      metadata.type = "character";
    }
    return this.setMetadata({ characterId, metadata }, overrides);
  }
  async setSocialToken({
    characterId,
    socialToken
  }, overrides = {}) {
    const hash = await this.base.contract.write.setSocialToken(
      [BigInt(characterId), socialToken],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async setPrimaryId({
    characterId
  }, overrides = {}) {
    const hash = await this.base.contract.write.setPrimaryCharacterId(
      [BigInt(characterId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async burn({
    characterId
  }, overrides = {}) {
    const hash = await this.base.contract.write.burn(
      [BigInt(characterId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async transfer({
    fromAddress,
    toAddress,
    characterId
  }, overrides = {}) {
    validateAddress(toAddress);
    const owner = fromAddress ?? this.base.walletClient.account.address;
    validateAddress(owner);
    const hash = await this.base.contract.write.safeTransferFrom(
      [owner, toAddress, BigInt(characterId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This returns the primary character ID of the given address.
   * @category Character
   * @returns The characterId of the primary character of the address.
   */
  async getPrimaryId({
    address
  }, overrides = {}) {
    validateAddress(address);
    const characterId = await this.base.contract.read.getPrimaryCharacterId(
      [address],
      overrides
    );
    return {
      data: characterId
    };
  }
  /**
   * This returns a boolean indicating whether the characterId is a primary characterId of an address.
   * @category Character
   * @returns A boolean value.
   */
  async isPrimaryId({
    /** The character ID of the character you want to check. */
    characterId
  }, overrides = {}) {
    const isPrimary = await this.base.contract.read.isPrimaryCharacter(
      [BigInt(characterId)],
      overrides
    );
    return {
      data: isPrimary
    };
  }
  /**
   * This returns a character given its handle.
   * @category Character
   * @returns The character with the given handle.
   */
  async getByHandle({
    handle
  }, overrides = {}) {
    const validates = validateHandleFormat(handle);
    if (!validates.valid) {
      throw new TypeError(validates.message);
    }
    handle = handle.toLowerCase();
    const character = await this.base.contract.read.getCharacterByHandle(
      [handle],
      overrides
    );
    const metadata = normalizeCharacterMetadata(
      character.uri ? await ipfsUriToMetadata(character.uri) : void 0
    );
    return {
      data: {
        characterId: character.characterId,
        handle: character.handle,
        uri: character.uri,
        metadata,
        socialToken: character.socialToken,
        noteCount: character.noteCount
      }
    };
  }
  /**
   * This returns a character given its characterId.
   * @category Character
   * @returns The character with the given characterId.
   */
  async get({
    characterId
  }, overrides = {}) {
    const character = await this.base.contract.read.getCharacter(
      [BigInt(characterId)],
      overrides
    );
    const { metadata } = await ipfsParseMetadataOrUri(
      "character",
      character.uri,
      true
    );
    return {
      data: {
        characterId: character.characterId,
        handle: character.handle,
        uri: character.uri,
        socialToken: character.socialToken,
        noteCount: character.noteCount,
        metadata
      }
    };
  }
  /**
   * This returns the handle of a character.
   * @category Character
   * @returns The handle of the character.
   */
  async getHandle({
    characterId
  }, overrides = {}) {
    const handle = await this.base.contract.read.getHandle(
      [BigInt(characterId)],
      overrides
    );
    return {
      data: handle
    };
  }
  /**
   * This returns the URI of a character.
   * @category Character
   * @returns The URI of the character.
   */
  async getUri({
    characterId
  }, overrides = {}) {
    const uri = await this.base.contract.read.getCharacterUri(
      [BigInt(characterId)],
      overrides
    );
    return {
      data: uri
    };
  }
  /**
   * This returns the character given a {@link create} transaction hash.
   * @category Character
   * @returns The characterId of the character that was created.
   */
  async getByTransaction({
    txHash
  }, overrides = {}) {
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      txHash
    );
    const parser = parseLog(receipt.logs, "CharacterCreated");
    const result = await this.get(
      { characterId: parser.args.characterId },
      overrides
    );
    return result;
  }
  /**
   * This checks if a character exists.
   * @category Character
   * @returns A boolean indicating whether the character exists.
   */
  async existsForAddress({
    address
  }, overrides = {}) {
    validateAddress(address);
    const characterId = await this.base.contract.read.getPrimaryCharacterId(
      [address],
      overrides
    );
    const exists = characterId !== 0n;
    return {
      data: exists
    };
  }
  /**
   * This checks if a character exists.
   * @category Character
   * @returns A boolean indicating whether the character exists.
   */
  async existsForHandle({
    handle
  }, overrides = {}) {
    const data = await this.base.contract.read.getCharacterByHandle(
      [handle],
      overrides
    );
    const exists = data.handle !== "";
    return {
      data: exists
    };
  }
  async withdrawFromNewbieVilla({
    toAddress,
    characterId,
    nonce,
    expires,
    proof
  }, overrides = {}) {
    validateAddress(toAddress);
    const hash = await this.base.newbieVillaContract.write.withdraw(
      [toAddress, BigInt(characterId), BigInt(nonce), BigInt(expires), proof],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  balanceOfNewbie({ characterId }, overrides = {}) {
    return this.base.newbieVillaContract.read.balanceOf(
      [BigInt(characterId)],
      overrides
    );
  }
};
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "create", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "setHandle", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "setUri", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "changeMetadata", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "setSocialToken", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "setPrimaryId", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "burn", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "transfer", 1);
__decorateClass([
  autoSwitchMainnet()
], CharacterContract.prototype, "withdrawFromNewbieVilla", 1);

// src/contract/subcontracts/operator.ts
var OperatorContract = class {
  constructor(base) {
    this.base = base;
  }
  async grantForCharacter({
    characterId,
    operator,
    permissions
  }, overrides = {}) {
    validateAddress(operator);
    const permissionUint256 = this.convertPermissionsToUint256ForCharacter(permissions);
    const hash = await this.base.contract.write.grantOperatorPermissions(
      [BigInt(characterId), operator, permissionUint256],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    const log = parseLog(receipt.logs, "GrantOperatorPermissions");
    return {
      data: {
        bitmapUint256: log.args.permissionBitMap
      },
      transactionHash: receipt.transactionHash
    };
  }
  async grantForNote({
    characterId,
    noteId,
    allowlist,
    blocklist = []
  }, overrides = {}) {
    validateAddress(allowlist);
    validateAddress(blocklist);
    const hash = await this.base.contract.write.grantOperators4Note(
      [BigInt(characterId), BigInt(noteId), blocklist, allowlist],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: {},
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This returns operators of the character.
   *
   * @category Operator
   * @returns The operators of the character.
   */
  async getForCharacter({
    characterId
  }, overrides = {}) {
    const operators = await this.base.contract.read.getOperators(
      [BigInt(characterId)],
      overrides
    );
    return {
      data: operators
    };
  }
  /**
   * This returns operators of the note.
   *
   * @category Operator
   */
  async getForNote({
    characterId,
    noteId
  }, overrides = {}) {
    const [allowlist, blocklist] = await this.base.contract.read.getOperators4Note(
      [BigInt(characterId), BigInt(noteId)],
      overrides
    );
    return {
      data: {
        allowlist,
        blocklist
      }
    };
  }
  /**
   * This checks if an operator is allowed to operate a note.
   *
   * @category Operator
   * @returns Whether the operator is allowed to operate the note.
   */
  async isAllowedForNote({
    characterId,
    noteId,
    operator
  }, overrides = {}) {
    const isAllowed = await this.base.contract.read.isOperatorAllowedForNote(
      [BigInt(characterId), BigInt(noteId), operator],
      overrides
    );
    return {
      data: isAllowed
    };
  }
  /**
   * This returns the permissions of an operator for a character.
   *
   * @category Operator
   * @returns The permissions of the operator.
   */
  async getPermissionsForCharacter({
    characterId,
    operator
  }, overrides = {}) {
    const permissionUint256 = await this.base.contract.read.getOperatorPermissions(
      [BigInt(characterId), operator],
      overrides
    );
    const permissions = this.convertUint256ToPermissionsForCharacter(permissionUint256);
    return {
      data: permissions
    };
  }
  /**
   * This converts the character operator permission constants array to a uint256.
   *
   * @category Operator
   * @returns The uint256.
   */
  convertPermissionsToUint256ForCharacter(permissions) {
    const bits = permissions.map((permission) => {
      const bit = Object.entries(CHARACTER_PERMISSION_BITMAP).find(
        ([, value]) => value === permission
      )?.[0];
      if (!bit) {
        throw new Error(`Found invalid permission: ${permission}.`);
      }
      return Number.parseInt(bit);
    });
    const uint256Array = Array.from({ length: 256 }).fill(0);
    for (const bit of bits) {
      uint256Array[bit] = 1;
    }
    uint256Array.reverse();
    const uint256Decimal = this.#convertBinaryBitsToUint256(uint256Array);
    return uint256Decimal;
  }
  /**
   * This converts the uint256 to the character operator permission constants array.
   *
   * @category Operator
   * @param permissionUint256 - The uint256.
   * @returns The permission constants array.
   */
  convertUint256ToPermissionsForCharacter(permissionUint256) {
    const binaryBits = this.#convertUint256ToBinaryBits(permissionUint256);
    const permissions = binaryBits.reverse().map((bit, index) => {
      if (bit === 1) {
        if (this.#isPermissionBitForCharacter(index)) {
          return CHARACTER_PERMISSION_BITMAP[index];
        }
        warn("Found invalid permission bit.", index);
      }
      return void 0;
    }).filter(Boolean);
    return permissions;
  }
  /**
   * This converts binary bits array to a uint256 in decimal.
   *
   * @category Operator
   * @param bits - The binary bits array.
   * @returns The uint256 in decimal.
   */
  #convertBinaryBitsToUint256(bits) {
    const n = bits.join("").replace(/^0+/, "").split("").reverse().reduce((acc, bit, index) => {
      if (bit === "1") {
        return acc + 2n ** BigInt(index);
      }
      return acc;
    }, 0n);
    return n;
  }
  /**
   * This converts a uint256 in decimal to binary bits array.
   *
   * @category Operator
   * @param uint256 - The uint256 in decimal.
   * @returns The binary bits array of length 256.
   */
  #convertUint256ToBinaryBits(uint256) {
    return uint256.toString(2).padStart(256, "0").split("").map((bit) => Number.parseInt(bit));
  }
  /**
   * This checks if the permission bit is for character.
   *
   * @category Operator
   * @param bit - The permission bit.
   * @returns true if the permission bit is for character; otherwise, false.
   */
  #isPermissionBitForCharacter(bit) {
    return Object.keys(CHARACTER_PERMISSION_BITMAP).includes(bit.toString());
  }
};
__decorateClass([
  autoSwitchMainnet()
], OperatorContract.prototype, "grantForCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], OperatorContract.prototype, "grantForNote", 1);
var CHARACTER_PERMISSION_BITMAP = {
  0: "SET_HANDLE",
  1: "SET_SOCIAL_TOKEN",
  2: "GRANT_OPERATOR_PERMISSIONS",
  3: "GRANT_OPERATORS_FOR_NOTE",
  176: "SET_CHARACTER_URI",
  177: "SET_LINKLIST_URI",
  178: "LINK_CHARACTER",
  179: "UNLINK_CHARACTER",
  180: "CREATE_THEN_LINK_CHARACTER",
  181: "LINK_NOTE",
  182: "UNLINK_NOTE",
  183: "LINK_ERC721",
  184: "UNLINK_ERC721",
  185: "LINK_ADDRESS",
  186: "UNLINK_ADDRESS",
  187: "LINK_ANY_URI",
  188: "UNLINK_ANY_URI",
  189: "LINK_LINKLIST",
  190: "UNLINK_LINKLIST",
  191: "SET_LINK_MODULE_FOR_CHARACTER",
  192: "SET_LINK_MODULE_FOR_NOTE",
  193: "SET_LINK_MODULE_FOR_LINKLIST",
  194: "SET_MINT_MODULE_FOR_NOTE",
  195: "SET_NOTE_URI",
  196: "LOCK_NOTE",
  197: "DELETE_NOTE",
  198: "POST_NOTE_FOR_CHARACTER",
  199: "POST_NOTE_FOR_ADDRESS",
  200: "POST_NOTE_FOR_LINKLIST",
  201: "POST_NOTE_FOR_NOTE",
  202: "POST_NOTE_FOR_ERC721",
  203: "POST_NOTE_FOR_ANY_URI",
  236: "POST_NOTE"
};

// src/contract/subcontracts/cbt.ts
var CbtContract = class {
  constructor(base) {
    this.base = base;
  }
  async mint({
    characterId,
    tokenId
  }, overrides = {}) {
    const hash = await this.base.cbtContract.write.mint(
      [BigInt(characterId), BigInt(tokenId)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async setTokenUri({
    tokenId,
    uri
  }, overrides = {}) {
    const hash = await this.base.cbtContract.write.setTokenURI(
      [BigInt(tokenId), uri],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This returns the URI of the token.
   * @category CharacterBoundToken
   * @returns The URI of the token.
   */
  async getTokenUri({
    tokenId
  }, overrides = {}) {
    const uri = await this.base.cbtContract.read.uri(
      [BigInt(tokenId)],
      overrides
    );
    return {
      data: uri
    };
  }
};
__decorateClass([
  autoSwitchMainnet()
], CbtContract.prototype, "mint", 1);
__decorateClass([
  autoSwitchMainnet()
], CbtContract.prototype, "setTokenUri", 1);
var TipsContract = class {
  constructor(base) {
    this.base = base;
  }
  async tipCharacter({
    fromCharacterId,
    toCharacterId,
    amount
  }, overrides = {}) {
    const data = encodeAbiParameters(
      [{ type: "uint256" }, { type: "uint256" }],
      [BigInt(fromCharacterId), BigInt(toCharacterId)]
    );
    const hash = await this.base.miraContract.write.send(
      [this.base.options.contractAddresses.tips, BigInt(amount), data],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async tipCharacterFromNewbieVilla({
    fromCharacterId,
    toCharacterId,
    amount
  }, overrides = {}) {
    const hash = await this.base.newbieVillaContract.write.tipCharacter(
      [BigInt(fromCharacterId), BigInt(toCharacterId), BigInt(amount)],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async tipCharacterForNoteFromNewbieVilla(fromCharacterId, toCharacterId, toNoteId, amount, overrides = {}) {
    const hash = await this.base.newbieVillaContract.write.tipCharacterForNote(
      [
        BigInt(fromCharacterId),
        BigInt(toCharacterId),
        BigInt(toNoteId),
        BigInt(amount)
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async tipCharacterForNote({
    fromCharacterId,
    toCharacterId,
    toNoteId,
    amount
  }, overrides = {}) {
    const data = encodeAbiParameters(
      [{ type: "uint256" }, { type: "uint256" }, { type: "uint256" }],
      [BigInt(fromCharacterId), BigInt(toCharacterId), BigInt(toNoteId)]
    );
    const hash = await this.base.miraContract.write.send(
      [this.base.options.contractAddresses.tips, BigInt(amount), data],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This gets the balance of $MIRA token of a character.
   *
   * If it is a normal character, this returns the balance of the address of the character.
   * If it is a newbie-village character, this returns the balance of the character in the contract.
   *
   * This throws if the character is not found.
   *
   * @category TipsContract
   * @returns The balance of $MIRA token of the address. Unit: wei.
   */
  async getBalanceOfCharacter({
    characterId
  }, overrides = {}) {
    const address = await this.base.contract.read.ownerOf(
      [BigInt(characterId)],
      overrides
    );
    const balance = isAddressEqual(
      address,
      this.base.options.contractAddresses.newbieVilla
    ) ? await this.base.newbieVillaContract.read.balanceOf(
      [BigInt(characterId)],
      overrides
    ) : await this.base.miraContract.read.balanceOf([address], overrides);
    return { data: balance };
  }
  /**
   * This gets the balance of $MIRA token of an address.
   *
   * @category TipsContract
   * @returns The balance of $MIRA token of the address. Unit: wei.
   */
  async getBalance({
    address
  }, overrides = {}) {
    const balance = await this.base.miraContract.read.balanceOf(
      [address],
      overrides
    );
    return {
      data: balance
    };
  }
  /**
   * This gets the token address of $MIRA.
   *
   * @category TipsContract
   * @returns The token address of $MIRA.
   */
  async getTokenAddress(overrides = {}) {
    const res = await this.base.tipsContract.read.getToken(overrides);
    return {
      data: res
    };
  }
  /**
   * This gets the token decimals of $MIRA.
   *
   * @category TipsContract
   * @returns The token decimals of $MIRA.
   */
  async getTokenDecimals(overrides = {}) {
    const res = await this.base.miraContract.read.decimals(overrides);
    return {
      data: res
    };
  }
};
__decorateClass([
  autoSwitchMainnet()
], TipsContract.prototype, "tipCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], TipsContract.prototype, "tipCharacterFromNewbieVilla", 1);
__decorateClass([
  autoSwitchMainnet()
], TipsContract.prototype, "tipCharacterForNoteFromNewbieVilla", 1);
__decorateClass([
  autoSwitchMainnet()
], TipsContract.prototype, "tipCharacterForNote", 1);

// src/contract/subcontracts/revision.ts
var CURRENT_SDK_REVISION = 4n;
var RevisionContract = class {
  constructor(base) {
    this.base = base;
  }
  /**
   * This returns the remote latest revision of the contract.
   * @category Revision
   * @returns The remote latest revision of the contract.
   */
  async getLatest(overrides = {}) {
    const revision = await this.base.contract.read.getRevision(overrides);
    return {
      data: revision
    };
  }
  /**
   * This returns the local SDK revision of the contract.
   * @category Revision
   * @returns The local SDK revision of the contract.
   */
  getCurrent() {
    return {
      data: CURRENT_SDK_REVISION
    };
  }
  /**
   * This checks if the SDK is up-to-date with the contract.
   * Although the contract will update its revision from time to time, it will keep compatibility with
   * the old revision. I.e. Crossbell's developers ensured that there is not going to be any breaking changes
   * when introducing new features in the future.
   *
   * Therefore, this method is used to check if you need to update your SDK to use some new features.
   * @category Revision
   * @returns Whether the SDK is up-to-date with the contract and the local/remote revision.
   */
  async check() {
    const { data: latestRevision } = await this.getLatest();
    const currentRevision = CURRENT_SDK_REVISION;
    const isUpToDate = latestRevision === currentRevision;
    return {
      data: {
        isUpToDate,
        currentRevision,
        latestRevision
      }
    };
  }
};

// src/contract/subcontracts/link_module.ts
var LinkModuleContract = class {
  constructor(base) {
    this.base = base;
  }
  async setForCharacter({
    characterId,
    linkModule
  }, overrides = {}) {
    const moduleConfig = await getModuleConfig(linkModule);
    const hash = await this.base.contract.write.setLinkModule4Character(
      [
        {
          characterId: BigInt(characterId),
          linkModule: moduleConfig.address,
          linkModuleInitData: moduleConfig.initData
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async setForNote({
    characterId,
    noteId,
    linkModule
  }, overrides = {}) {
    const moduleConfig = await getModuleConfig(linkModule);
    const hash = await this.base.contract.write.setLinkModule4Note(
      [
        {
          characterId: BigInt(characterId),
          noteId: BigInt(noteId),
          linkModule: moduleConfig.address,
          linkModuleInitData: moduleConfig.initData
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This sets the link module for an address.
   * @category LinkModule
   * @param address The address to set the link module for.
   * @param linkModule The link module to set.
   * @returns The transaction hash.
   */
  // @autoSwitchMainnet()
  // async setForAddress(
  //   {
  //     address,
  //     linkModule,
  //   }: { address: Address; linkModule: MintOrLinkModuleConfig },
  //   overrides: WriteOverrides<Entry, 'setLinkModule4Address'> = {},
  // ): Promise<Result<undefined, true>> {
  //   const moduleConfig = await getModuleConfig(linkModule)
  //   const hash = await this.base.contract.write.setLinkModule4Address(
  //     [
  //       {
  //         account: address,
  //         linkModule: moduleConfig.address,
  //         linkModuleInitData: moduleConfig.initData,
  //       },
  //     ],
  //     overrides,
  //   )
  //   const receipt = await waitForTransactionReceiptWithRetry(
  //     this.base.publicClient,
  //     hash,
  //   )
  //   return {
  //     data: undefined,
  //     transactionHash: receipt.transactionHash,
  //   }
  // }
  /**
   * This sets the link module for a linklist.
   * @category LinkModule
   * @param linklistId The linklist ID to set the link module for.
   * @param linkModule The link module to set.
   * @returns The transaction hash.
   */
  // @autoSwitchMainnet()
  // async setForLinklist(
  //   {
  //     linklistId,
  //     linkModule,
  //   }: { linklistId: Numberish; linkModule: MintOrLinkModuleConfig },
  //   overrides: WriteOverrides<Entry, 'setLinkModule4Linklist'> = {},
  // ): Promise<Result<undefined, true>> {
  //   const moduleConfig = await getModuleConfig(linkModule)
  //   const hash = await this.base.contract.write.setLinkModule4Linklist(
  //     [
  //       {
  //         linklistId: BigInt(linklistId),
  //         linkModule: moduleConfig.address,
  //         linkModuleInitData: moduleConfig.initData,
  //       },
  //     ],
  //     overrides,
  //   )
  //   const receipt = await waitForTransactionReceiptWithRetry(
  //     this.base.publicClient,
  //     hash,
  //   )
  //   return {
  //     data: undefined,
  //     transactionHash: receipt.transactionHash,
  //   }
  // }
};
__decorateClass([
  autoSwitchMainnet()
], LinkModuleContract.prototype, "setForCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], LinkModuleContract.prototype, "setForNote", 1);

// src/contract/subcontracts/mint_module.ts
var MintModuleContract = class {
  constructor(base) {
    this.base = base;
  }
  async setForNote({
    characterId,
    noteId,
    mintModule
  }, overrides = {}) {
    const moduleConfig = await getModuleConfig(mintModule);
    const hash = await this.base.contract.write.setMintModule4Note(
      [
        {
          characterId: BigInt(characterId),
          noteId: BigInt(noteId),
          mintModule: moduleConfig.address,
          mintModuleInitData: moduleConfig.initData
        }
      ],
      overrides
    );
    const receipt = await waitForTransactionReceiptWithRetry(
      this.base.publicClient,
      hash
    );
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
};
__decorateClass([
  autoSwitchMainnet()
], MintModuleContract.prototype, "setForNote", 1);
var TipsWithFeeContract = class {
  constructor(base) {
    this.base = base;
  }
  async tipCharacter({
    fromCharacterId,
    toCharacterId,
    amount,
    feeReceiver
  }, overrides = {}) {
    const data = encodeAbiParameters(
      [{ type: "uint256" }, { type: "uint256" }, { type: "address" }],
      [BigInt(fromCharacterId), BigInt(toCharacterId), feeReceiver]
    );
    const hash = await this.base.miraContract.write.send(
      [this.base.options.contractAddresses.tipsWithFee, BigInt(amount), data],
      overrides
    );
    const receipt = await this.base.publicClient.waitForTransactionReceipt({
      hash
    });
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  async tipCharacterForNote({
    fromCharacterId,
    toCharacterId,
    toNoteId,
    amount,
    feeReceiver
  }, overrides = {}) {
    const data = encodeAbiParameters(
      [
        { type: "uint256" },
        { type: "uint256" },
        { type: "uint256" },
        { type: "address" }
      ],
      [
        BigInt(fromCharacterId),
        BigInt(toCharacterId),
        BigInt(toNoteId),
        feeReceiver
      ]
    );
    const hash = await this.base.miraContract.write.send(
      [this.base.options.contractAddresses.tipsWithFee, BigInt(amount), data],
      overrides
    );
    const receipt = await this.base.publicClient.waitForTransactionReceipt({
      hash
    });
    return {
      data: void 0,
      transactionHash: receipt.transactionHash
    };
  }
  /**
   * This gets the fee percentage of specific <receiver, note>.
   *
   * @category TipsWithFeeContract
   * @returns The fee percentage of specific <receiver, note>.
   */
  async getFeeFraction(feeReceiver, characterId, noteId, overrides = {}) {
    const res = await this.base.tipsWithFeeContract.read.getFeeFraction(
      [feeReceiver, BigInt(characterId), BigInt(noteId)],
      overrides
    );
    return {
      data: res
    };
  }
  /**
   * This gets the fee is owed by <feeFraction, tipAmount>.
   *
   * @category TipsWithFeeContract
   * @returns The fee is owed by <feeFraction, tipAmount>.
   */
  async getFeeAmount(feeReceiver, characterId, noteId, tipAmount, overrides = {}) {
    const res = await this.base.tipsWithFeeContract.read.getFeeAmount(
      [feeReceiver, BigInt(characterId), BigInt(noteId), BigInt(tipAmount)],
      overrides
    );
    return {
      data: res
    };
  }
};
__decorateClass([
  autoSwitchMainnet()
], TipsWithFeeContract.prototype, "tipCharacter", 1);
__decorateClass([
  autoSwitchMainnet()
], TipsWithFeeContract.prototype, "tipCharacterForNote", 1);

// src/contract/contract.ts
var Contract = class extends BaseContract {
  csb = new CsbContract(this);
  character = new CharacterContract(this);
  link = new LinkContract(this);
  cbt = new CbtContract(this);
  note = new NoteContract(this);
  operator = new OperatorContract(this);
  tips = new TipsContract(this);
  tipsWithFee = new TipsWithFeeContract(this);
  revision = new RevisionContract(this);
  linkModule = new LinkModuleContract(this);
  mintModule = new MintModuleContract(this);
};
function createContract(providerOrPrivateKey, options) {
  return new Contract(providerOrPrivateKey, options);
}

export { Contract, createContract, subcontracts_exports };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-YUCQM6KJ.mjs.map